import React from "react";
import Flatpickr from "react-flatpickr";
import { Modal, Spinner } from "reactstrap";

const HolidayModal = ({ loading, show, mode, formData, setFormData, onSubmit, onClose }) => {
  if (!show) return null;

  const isView = mode === "view";
  const isEdit = mode === "edit";
  const isCreate = mode === "create";

  const minStartDate = React.useMemo(() => {
    return new Date().fp_incr(3);
  }, []);

  const minNotificationDate = React.useMemo(() => {
    const now = new Date();
    const cutoff = new Date();
    cutoff.setHours(9, 40, 0, 0);

    if (!isCreate) return undefined;

    return now < cutoff ? "today" : new Date().fp_incr(1);
  }, [isCreate]);

  return (
    <Modal isOpen={show} toggle={onClose} backdrop="static" keyboard={false}>
      <form
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            e.currentTarget.requestSubmit();
          }
        }}
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
      >
        <div className="modal-header">
          <h5 className="modal-title">
            {isCreate && "Create Holiday"}
            {isView && "View Holiday"}
            {isEdit && "Edit Holiday"}
          </h5>
          <button
            type="button"
            className="btn-close"
            onClick={onClose}
          ></button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="text-center my-4">
              <Spinner color="primary" />
            </div>
          ) : (
            <>
              {/* Title */}
              <div className="mb-3">
                <label className="form-label">Title*</label>
                <input
                  type="text"
                  disabled={isView}
                  className="form-control"
                  value={formData.Title || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      Title: e.target.value,
                    })
                  }
                  required
                />
              </div>

              {/* Description */}
              <div className="mb-3">
                <label className="form-label">Description*</label>
                <textarea
                  disabled={isView}
                  className="form-control"
                  rows={3}
                  value={formData.Description || ""}
                  required
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      Description: e.target.value,
                    })
                  }
                />
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Start Date*</label>
                  <div className="input-group">
                    <Flatpickr
                      className="form-control"
                      disabled={isView}
                      value={formData.StartDate || ""}
                      options={{
                        dateFormat: "d-m-Y",
                        disableMobile: true,
                        minDate: formData.StartDate || minStartDate,
                      }}
                      required
                      onChange={(selectedDates, dateStr) =>
                        setFormData({
                          ...formData,
                          StartDate: dateStr,
                          EndDate:
                            formData.EndDate &&
                              new Date(formData.EndDate) < new Date(dateStr)
                              ? dateStr
                              : formData.EndDate,
                        })
                      }
                    />
                    <div className="input-group-append">
                      <span className="input-group-text">
                        <i className="mdi mdi-calendar" />
                      </span>
                    </div>
                  </div>
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">End Date*</label>
                  <div className="input-group">
                    <Flatpickr
                      className="form-control"
                      disabled={isView}
                      value={formData.EndDate || ""}
                      options={{
                        dateFormat: "d-m-Y",
                        disableMobile: true,
                        minDate: formData.StartDate || "today",
                      }}
                      required
                      onChange={(selectedDates, dateStr) =>
                        setFormData({
                          ...formData,
                          EndDate: dateStr,
                        })
                      }
                    />
                    <div className="input-group-append">
                      <span className="input-group-text">
                        <i className="mdi mdi-calendar" />
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Notification Date*</label>
                <div className="input-group">
                  <Flatpickr
                    className="form-control"
                    disabled={isView}
                    value={formData.NotificationDate || ""}
                    options={{
                      dateFormat: "d-m-Y",
                      disableMobile: true,
                      minDate: minNotificationDate,
                      maxDate: formData.StartDate || "today",
                    }}
                    required
                    onChange={(selectedDates, dateStr) =>
                      setFormData({
                        ...formData,
                        NotificationDate: dateStr,
                      })
                    }
                  />
                  <div className="input-group-append">
                    <span className="input-group-text">
                      <i className="mdi mdi-calendar" />
                    </span>
                  </div>
                </div>
              </div>

              <div className="form-check mb-2">
                <input
                  type="checkbox"
                  disabled={isView}
                  className="form-check-input"
                  id="CompOff"
                  checked={!!formData.IsCompGiven}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      IsCompGiven: e.target.checked,
                    })
                  }
                />
                <label className="form-check-label" htmlFor="CompOff">
                  Give Comp
                </label>
              </div>

              <div className="form-check">
                <input
                  type="checkbox"
                  disabled={isView}
                  className="form-check-input"
                  id="IsActive"
                  checked={!!formData.IsActive}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      IsActive: e.target.checked,
                    })
                  }
                />
                <label className="form-check-label" htmlFor="IsActive">
                  Is Active
                </label>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
          >
            Close
          </button>

          {!isView && (
            <button
              className="btn btn-primary"
              type="submit"
              disabled={loading}
            >
              {isCreate ? "Create" : "Save Changes"}
            </button>
          )}
        </div>
      </form>
    </Modal>
  );
};

export default HolidayModal;